import { Link } from "@tanstack/react-router";
import { Button } from "@wags/ui/button.tsx";
import { cn } from "@wags/ui/ui-utils";
import { VercelImage } from "@wags/ui/vercel-image.tsx";
import { type ComponentProps, type ReactNode, useState } from "react";
import ColliePeekingIllustration from "@/assets/illustrations/collie-peeking.svg";
import KitsWaitingIllustration from "@/assets/illustrations/kits-concierge-dog.svg";
import KitsKeyIllustration from "@/assets/illustrations/kits-key.svg";
import PupJumpIllustration from "@/assets/illustrations/pup-jump.svg";
import ShibaSideProfileIllustration from "@/assets/illustrations/shiba-side-profile.svg";
import { KitsSweatIllustration } from "@/components/shared/KitsSweatIllustration";
import "@/components/shared/error-state-motion.css";

type ErrorSceneMood = "detour" | "lost" | "protected" | "searching" | "waiting";

interface PageErrorStateProps extends Omit<
    ComponentProps<"div">,
    "title" | "children"
> {
    title?: string;
    description?: string;
    onRetry?: () => void;
    actions?: ReactNode;
    className?: string;
    scene?: ErrorSceneMood;
    headingLevel?: 1 | 2;
}

export function PageErrorState({
    title = "This page didn't load",
    description = "Try loading the page again.",
    onRetry,
    actions,
    className,
    scene = "detour",
    headingLevel = 1,
    ...props
}: PageErrorStateProps) {
    const Heading = headingLevel === 1 ? "h1" : "h2";

    return (
        <div
            {...props}
            className={cn(
                "flex min-h-[72vh] w-full items-center justify-center px-6 py-12",
                className,
            )}
        >
            <div className="error-state-enter error-state-from-y flex max-w-md flex-col items-center text-center">
                <ErrorScene mood={scene} />
                <Heading className="mt-8 text-page-title text-balance text-doberman-black">
                    {title}
                </Heading>
                <p className="mt-4 text-body leading-[1.55] text-pretty text-doberman-black/60">
                    {description}
                </p>
                {(onRetry || actions) && (
                    <div className="mt-8 flex flex-wrap items-center justify-center gap-3 [&_a]:min-h-11 [&_button]:min-h-11">
                        {onRetry && <RetryButton onRetry={onRetry} />}
                        {actions}
                    </div>
                )}
            </div>
        </div>
    );
}

function ErrorScene({
    mood,
    className,
}: {
    mood: ErrorSceneMood;
    className?: string;
}) {
    if (mood === "detour") {
        return (
            <div className={cn("h-[72px]", className)} aria-hidden>
                <KitsSweatIllustration className="h-[72px] w-auto" />
            </div>
        );
    }

    const scene = {
        lost: {
            src: PupJumpIllustration,
            width: 537,
            height: 212,
            className: "h-auto w-[156px] sm:w-[168px]",
            enterClass: "error-state-from-lost",
        },
        protected: {
            src: KitsKeyIllustration,
            width: 317,
            height: 305,
            className: "h-[84px] w-auto",
            enterClass: "error-state-from-protected",
        },
        searching: {
            src: ShibaSideProfileIllustration,
            width: 43,
            height: 45,
            className: "h-[84px] w-auto -scale-x-100",
            enterClass: "error-state-from-searching",
        },
        waiting: {
            src: KitsWaitingIllustration,
            width: 199,
            height: 174,
            className: "h-[80px] w-auto",
            enterClass: "error-state-from-waiting",
        },
    }[mood];

    return (
        <div
            className={cn(
                "error-state-enter flex h-[88px] items-end justify-center",
                scene.enterClass,
                className,
            )}
            aria-hidden
        >
            <VercelImage
                src={scene.src}
                alt=""
                width={scene.width}
                height={scene.height}
                className={scene.className}
            />
        </div>
    );
}

interface SectionErrorStateProps {
    title?: string;
    description: string;
    onRetry: () => void;
    className?: string;
}

export function SectionErrorState({
    title = "This section didn't load",
    description,
    onRetry,
    className,
}: SectionErrorStateProps) {
    return (
        <div
            className={cn(
                "rounded-card border border-doberman-black/10 bg-pug-beige/35 p-card",
                className,
            )}
        >
            <div className="mb-4 w-11 sm:w-12" aria-hidden>
                <VercelImage
                    src={ColliePeekingIllustration}
                    alt=""
                    width={140}
                    height={185}
                    className="h-auto w-full"
                />
            </div>
            <h2 className="text-section-title text-doberman-black text-balance">
                {title}
            </h2>
            <p className="mt-2 max-w-xl text-body leading-[1.55] text-doberman-black/60 text-pretty">
                {description}
            </p>
            <RetryButton className="mt-5" variant="outline" onRetry={onRetry} />
        </div>
    );
}

function RetryButton({
    onRetry,
    variant = "default",
    className,
}: {
    onRetry: () => void;
    variant?: "default" | "outline";
    className?: string;
}) {
    const [isRetrying, setIsRetrying] = useState(false);

    const retry = () => {
        if (isRetrying) return;
        setIsRetrying(true);
        void Promise.all([
            Promise.resolve().then(onRetry),
            new Promise((resolve) => window.setTimeout(resolve, 550)),
        ]).then(
            () => setIsRetrying(false),
            () => setIsRetrying(false),
        );
    };

    return (
        <Button
            className={cn("h-11 min-w-[7.25rem] px-6", className)}
            variant={variant}
            onClick={retry}
            disabled={isRetrying}
            aria-busy={isRetrying}
        >
            {isRetrying ? "Checking again…" : "Try again"}
        </Button>
    );
}

export function ReturnToFindAction() {
    return (
        <Button asChild variant="outline" size="lg" className="h-11">
            <Link to="/find">Return to find</Link>
        </Button>
    );
}
